import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LocationSearch } from "@/components/LocationSearch";
import { btnGhost, btnPrimary, inputCls } from "@/components/TopBar";

export const HaltPointDialog = ({ vehicle, onClose, onSaved }) => {
  const trip = vehicle.active_trip;
  const [place, setPlace] = useState(
    trip?.halt_pos ? { name: trip.halt_name, lat: trip.halt_pos.lat, lng: trip.halt_pos.lng } : null
  );
  const [useCustom, setUseCustom] = useState(false);
  const [lat, setLat] = useState(trip?.halt_pos?.lat ?? "");
  const [lng, setLng] = useState(trip?.halt_pos?.lng ?? "");
  const [radius, setRadius] = useState(trip?.halt_radius_m ?? 300);
  const [busy, setBusy] = useState(false);

  const save = async (clear) => {
    const pos = clear
      ? null
      : useCustom
      ? { lat: Number(lat), lng: Number(lng) }
      : place && { lat: place.lat, lng: place.lng };
    if (!clear && (!pos || Number.isNaN(pos.lat) || Number.isNaN(pos.lng))) {
      toast.error("Search a place or enter valid coordinates");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/trips/${trip.id}/halt-point`, {
        name: clear ? null : useCustom ? `${lat}, ${lng}` : place.name,
        pos,
        radius_m: Number(radius) || 300,
      });
      toast.success(clear ? "Halt point cleared" : "Halt point set - truck will park on arrival");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1600] flex items-start justify-center overflow-y-auto p-4 py-10"
      data-testid="halt-point-dialog"
    >
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
        className="relative w-full max-w-md border border-white/15 bg-[#0A0A0A] p-6 rise"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="close-halt-point-btn"
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">arrival rule</p>
        <h2 className="font-head text-2xl font-black tracking-tight">HALT POINT</h2>
        <p className="mt-2 text-[11px] text-[#A1A1AA]">
          {vehicle.plate_number} parks itself the moment it reaches this point.
        </p>

        <div className="mt-6 space-y-4">
          <label className="flex items-center gap-2 text-[10px] tracking-[0.2em] text-[#A1A1AA]">
            <input
              type="checkbox"
              data-testid="halt-custom-toggle"
              checked={useCustom}
              onChange={(e) => setUseCustom(e.target.checked)}
              className="h-3 w-3 accent-[#00F0FF]"
            />
            ENTER RAW COORDINATES
          </label>

          {useCustom ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-2 block">latitude</label>
                <input data-testid="halt-lat-input" value={lat} onChange={(e) => setLat(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="overline mb-2 block">longitude</label>
                <input data-testid="halt-lng-input" value={lng} onChange={(e) => setLng(e.target.value)} className={inputCls} />
              </div>
            </div>
          ) : (
            <LocationSearch label="halt at" value={place} onChange={setPlace} testid="halt-location" />
          )}

          <div>
            <label className="overline mb-2 block">trigger radius (m)</label>
            <input
              data-testid="halt-radius-input"
              type="number"
              min="50"
              max="5000"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="submit-halt-point-btn" className={`${btnPrimary} mt-6 w-full py-3`}>
          {busy ? "saving" : "set halt point"}
        </button>
        {trip?.halt_pos && (
          <button
            type="button"
            disabled={busy}
            data-testid="clear-halt-point-btn"
            onClick={() => save(true)}
            className={`${btnGhost} mt-3 w-full py-2`}
          >
            CLEAR HALT POINT
          </button>
        )}
      </form>
    </div>
  );
};
